import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTheme, styled } from "@mui/material/styles";
import { matjungelenAmber, matjungelenDarkGreen } from "./colors";
import {
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  useMediaQuery,
  MobileStepper,
  Box,
} from "@mui/material";
import { StepIconProps } from "@mui/material/StepIcon";

export const TOTAL_STEPS = 6;

export enum StyledStepperMode {
  HORIZONTAL,
  VERTICAL,
}

interface StyledStepperProps {
  mode: StyledStepperMode;
  entityType: string;
  activeStep: number;
  onChangeStep: CallableFunction;
}

interface StepperProps {
  steps: string[];
  activeStep: number;
  onChangeStep: CallableFunction;
}

const CheckCircleIconGreen = () => <CheckCircleIcon color="primary" />;

const VerticalStepIcon = (props: StepIconProps) => {
  const { active, completed, icon } = props;
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        background: matjungelenAmber,
        color: matjungelenDarkGreen,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: "0.85rem",
        border: active ? `2px solid ${matjungelenDarkGreen}` : "none",
        boxSizing: "border-box",
      }}
    >
      {completed ? <CheckIcon sx={{ fontSize: 18 }} /> : icon}
    </Box>
  );
};

const VerticalConnector = styled(StepConnector)({
  marginLeft: "19px",
  "& .MuiStepConnector-line": {
    borderColor: "rgba(251, 245, 232, 0.6)",
    borderLeftWidth: "2px",
    minHeight: "24px",
  },
});

const VerticalStepper = (props: StepperProps) => {
  const { steps, activeStep } = props;

  return (
    <Stepper
      orientation="vertical"
      activeStep={Math.min(TOTAL_STEPS - 1, activeStep)}
      connector={<VerticalConnector />}
      sx={{ pt: 2, pb: 3 }}
    >
      {steps.map((label, idx) => {
        const isActive = Math.min(TOTAL_STEPS - 1, activeStep) === idx;
        const isClickable = activeStep > idx && activeStep < TOTAL_STEPS;
        const handleOnClick = isClickable
          ? () => props.onChangeStep(idx)
          : undefined;
        return (
          <Step key={label}>
            <StepLabel
              onClick={handleOnClick}
              StepIconComponent={VerticalStepIcon}
              sx={{
                padding: "6px",
                borderRadius: "999px",
                width: "fit-content",
                cursor: isClickable ? "pointer" : "default",
                ...(isActive && {
                  background: matjungelenAmber,
                  paddingRight: "20px",
                }),
                "& .MuiStepLabel-iconContainer": {
                  paddingRight: "12px",
                },
                "& .MuiStepLabel-label": {
                  color: matjungelenAmber,
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "20px",
                  letterSpacing: 0,
                },
                "& .MuiStepLabel-label.Mui-completed": {
                  color: matjungelenAmber,
                  fontWeight: 400,
                },
                "& .MuiStepLabel-label.Mui-active": {
                  color: matjungelenDarkGreen,
                  fontWeight: 400,
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
};

const HorizontalStepper = (props: StepperProps) => {
  const { steps, activeStep } = props;
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const isXs = useMediaQuery("(max-width:400px)");
  const alternativeLabel = useMediaQuery("(max-width:900px)");

  const showLabel = (s: number): boolean => {
    if (isSm) return false;
    return true;
  };

  const getStepIcon = (idx: number) => {
    if (activeStep > 4 && idx < 5) {
      return CheckCircleIconGreen;
    } else {
      return undefined;
    }
  };

  if (isXs) {
    return (
      <MobileStepper
        variant="dots"
        steps={5}
        position="static"
        activeStep={activeStep}
        nextButton={<Box component="div"></Box>}
        backButton={<Box component="div"></Box>}
        sx={{ background: "transparent", margin: "15px 0px" }}
      />
    );
  } else {
    return (
      <Stepper
        activeStep={Math.min(4, activeStep)}
        sx={{ pt: 3, pb: 5 }}
        alternativeLabel={alternativeLabel}
      >
        {steps.map((label, idx) => (
          <Step key={label}>
            <StepLabel StepIconComponent={getStepIcon(idx)}>
              {showLabel(idx) ? label : ""}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    );
  }
};

export const StyledStepper = (props: StyledStepperProps) => {
  const { mode, entityType, activeStep } = props;
  let steps = [
    "Organisasjon",
    "Organisasjon",
    "Detaljer",
    "Litt om deg",
    "Leder",
    "Oppsummering",
  ];
  if (entityType === "SKOLE") {
    steps = [
      "Organisasjon",
      "Finn skole",
      "Detaljer",
      "Litt om deg",
      "Rektor",
      "Oppsummering",
    ];
  } else {
    steps = [
      "Organisasjon",
      "Finn barnehage",
      "Detaljer",
      "Litt om deg",
      "Styrer",
      "Oppsummering",
    ];
  }

  if (mode === StyledStepperMode.VERTICAL) {
    return (
      <VerticalStepper
        activeStep={activeStep}
        steps={steps}
        onChangeStep={props.onChangeStep}
      />
    );
  } else {
    return (
      <HorizontalStepper
        activeStep={activeStep}
        steps={steps}
        onChangeStep={props.onChangeStep}
      />
    );
  }
};
